/* LEGEND — floating live RISK / REWARD card (RR Preview Feed).
   Renders the worker's rr-update frame: FLAT ("if I bought now") and POSITION
   ("open-trade truth + per-size scale-in consequences") modes. Frames carry
   STRUCTURE only — the live R / open-R / remaining-RR ratios are computed here
   from the ticking last price. Handles warming, NONE/STOP_ONLY, degraded,
   stale and inverted states. Draggable / closable, modeled on PblCard (the
   parent owns the getRr hydrate + subscribeRr live feed and passes `frame`).
   Payload spec: architecture/rr-preview-feed.md */
import React from 'react';
import { Icon } from './Widgets';
import { fmtPx4, fmtQty, etClock } from '../lib/format';
import { getRr, subscribeRr } from '../data/source';

const { useRef: useRefR, useState: useStateR, useEffect: useEffectR } = React;
const h = React.createElement;
const F = React.Fragment;

const RR_STOP_LABEL: Record<string, string> = {
  defended_1m_ema9: 'defended 1m EMA9', vwap_reclaim: 'VWAP reclaim', prior_day_high: 'prior-day high',
  fallback_cascade: 'fallback cascade', defended_pm_low: 'defended PM low', defended_vwap: 'defended VWAP',
  entry: 'entry stop', manual_move: 'manual move', trailing: 'trailing', breakeven: 'break-even',
  scale_in_recalc: 'scale recalc', kept_existing: 'kept existing',
};
const SIZE_ORDER = ['lotto', 'small', 'half', 'full', 'double'];
const usd0 = (v: number) => (v < 0 ? '−$' : '$') + Math.abs(Math.round(v)).toLocaleString('en-US');

function RrCardInner(props: any) {
  const { symbol, lastPrice, pos, onMove, onClose } = props;
  // Perf receipt (window.__rrPerf): body→commit ms + render count, so a
  // slowdown claim is checkable in devtools — the VisionLayer pattern.
  const t0 = performance.now();
  const cardRef = useRefR<any>(null);
  // The card OWNS its hydrate+subscribe (HeatStrip rule): rr-update frames
  // arrive up to ~12/min, and holding them in Terminal/ChartWindow state would
  // re-render the whole tree per frame — even with the card closed. Here a
  // frame re-renders exactly this card, and only while it is mounted.
  const [liveFrame, setFrame] = useStateR<any>(null);
  useEffectR(() => {
    let alive = true;
    setFrame(null); // clear on symbol switch — never render old stops on a new symbol
    getRr(symbol).then((r: any) => {
      // Never clobber a fresher live frame with the hydrate snapshot: the WS
      // handler below registers synchronously and can land first.
      if (alive && r && r.active && r.frame) {
        setFrame((prev: any) => (prev && prev.seq >= r.frame.seq ? prev : r.frame));
      }
    }).catch(() => {});
    const off = subscribeRr(symbol, (fr: any) => { if (alive && fr) setFrame(fr); });
    return () => { alive = false; off(); };
  }, [symbol]);
  useEffectR(() => {
    const w: any = typeof window !== 'undefined' ? window : null;
    if (!w) return;
    const P = w.__rrPerf || (w.__rrPerf = { renders: 0, msEma: 0, msMax: 0 });
    const ms = performance.now() - t0;
    P.renders++;
    P.msEma = P.msEma ? P.msEma * 0.9 + ms * 0.1 : ms;
    if (ms > P.msMax) P.msMax = ms;
  });
  // Bench seam: scripts/rr-bench.cjs renders the card server-side, where
  // effects (and so the live feed) never run — it supplies the frame directly.
  const frame = props.frameOverride !== undefined ? props.frameOverride : liveFrame;
  const f = frame || {};
  // Staleness heartbeat: with a dead feed AND a quiet tape nothing else forces a
  // render, so the 90s stale badge could never appear (esp. in detached windows,
  // whose parents are memo-blocked). One slow tick keeps Date.now() honest.
  const [, setStaleTick] = useStateR(0);
  useEffectR(() => {
    const id = setInterval(() => setStaleTick((t: number) => t + 1), 30000);
    return () => clearInterval(id);
  }, []);
  const nowMs = Date.now();

  // Nothing to show until this symbol's feed produces a frame (also guards the
  // one-paint stale-symbol window around a switch).
  if (!frame || frame.symbol !== symbol) return null;

  function startDrag(e: any) {
    if (e.target.closest('button') || e.target.closest('a')) return;
    e.preventDefault();
    // pos.x == null means right-anchored: resolve the real left edge before dragging.
    const ox = pos.x == null && cardRef.current ? cardRef.current.offsetLeft : pos.x || 0;
    const sx = e.clientX, sy = e.clientY, oy = pos.y;
    const move = (ev: any) => onMove({ x: ox + (ev.clientX - sx), y: oy + (ev.clientY - sy) });
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  }

  const cardStyle =
    pos.x == null ? { right: '14px', top: pos.y + 'px' } : { left: pos.x + 'px', top: pos.y + 'px' };

  const head = (badges: any) =>
    h('div', { className: 'pc-head', onPointerDown: startDrag },
      h('span', { className: 'pc-sym' }, symbol),
      h('span', { className: 'rr-tag' }, 'Risk / Reward'),
      badges,
      h('div', { className: 'pc-actions' },
        h('button', { className: 'icon-btn sm', onClick: onClose, title: 'Close card' }, Icon.x())));

  // ---- WARMING ----
  if (f.state === 'warming') {
    const why = f.reason === 'level_map'
      ? 'Level map not built yet — waiting on structure before it can place a stop.'
      : 'Indicators still seeding — no stop or targets until the 1m studies are ready.';
    return h('div', { className: 'pos-card rr-card', style: cardStyle, ref: cardRef },
      head(h('span', { className: 'rr-badge warm' }, 'WARMING')),
      h('div', { className: 'pc-body' },
        h('div', { className: 'rr-warming' },
          h('div', { className: 'rr-spin' }),
          h('div', { className: 'rw-t' }, 'Warming up'),
          h('div', { className: 'rw-s' }, why))));
  }

  const ep = f.entryPreview;
  if (!ep || !ep.anchor || !ep.stop) return null; // malformed frame — render nothing
  const targets: any[] = ep.targets || [];
  // With a stop-only ACTIVE preset the worker still computes the ladder under a
  // labeled reference preset (ep.refPreset) — the R/R answer exists regardless
  // of whether target orders would be placed. STOP_ONLY only renders when the
  // frame genuinely carries no ladder.
  const none = ep.targetMode === 'STOP_ONLY' || !targets.length;
  const degraded =
    /fallback_cascade|stranded/.test(ep.stop.type || '') ||
    (ep.targetMode !== 'CHART_BASED' && ep.targetMode !== 'STOP_ONLY');
  const stale = nowMs - f.ts > 90000;
  const isPos = f.mode === 'position' && f.position;

  const badges = h(F, null,
    h('span', { className: 'rr-badge ' + (isPos ? 'pos' : 'flat') }, isPos ? 'POSITION' : ep.preset),
    ep.refPreset ? h('span', { className: 'rr-badge flat', title: 'Active preset is stop-only — ladder computed under ' + ep.refPreset + ' as an R/R reference; a buy places NO target orders' }, 'ref ' + ep.refPreset) : null,
    degraded ? h('span', { className: 'rr-badge caution', title: 'Degraded: ' + ep.targetMode + ' / ' + ep.stop.type }, 'DEGRADED') : null,
    stale ? h('span', { className: 'rr-badge stale' }, 'as of ' + etClock(f.ts)) : null);

  // ============================ FLAT ============================
  if (!isPos) {
    // MV-5 expects none of the walls to be reached: a buy now places NO target orders (worker rr-preview, 2026-10-22)
    const noneReach = ep.targetMode === 'NONE_REACHABLE';
    const t1 = targets[0];
    const invFlat = lastPrice > 0 && lastPrice <= ep.stop.price;
    const liveR = t1 && !invFlat && lastPrice > ep.stop.price
      ? (t1.price - lastPrice) / (lastPrice - ep.stop.price)
      : null;
    const pass = ep.blendedRR != null ? ep.blendedRR >= ep.minRR : null;
    const rewFlex = Math.max(0.5, Math.min(ep.blendedRR || 1, 8));

    return h('div', { className: 'pos-card rr-card' + (stale ? ' rr-stale' : ''), style: cardStyle, ref: cardRef },
      head(badges),
      h('div', { className: 'pc-body rr-body' },
        none
          ? h('div', { className: 'rr-hero none' },
              h('div', { className: 'rr-none-badge' }, 'STOP-ONLY'),
              h('div', { className: 'rr-none-sub' }, 'NONE preset — no target ladder by definition. Manage the exit off the stop.'))
          : noneReach
          ? h('div', { className: 'rr-hero none' },
              h('div', { className: 'rr-none-badge' }, 'NO REACHABLE WALL'),
              h('div', { className: 'rr-none-sub' }, 'MV-5 expects none of the walls on this side to be reached within the hour — a buy now places NO target orders.'))
          : h('div', { className: 'rr-hero' },
              h('div', { className: 'rr-hero-main' },
                h('span', { className: 'rr-blend num ' + (pass ? 'up' : 'down') }, ep.blendedRR.toFixed(2)),
                h('span', { className: 'rr-blend-u' }, 'R'),
                h('span', { className: 'rr-pass ' + (pass ? 'ok' : 'bad') }, pass ? 'PASS' : 'REJECT')),
              h('div', { className: 'rr-hero-sub' },
                h('span', null, 'blended · min ', h('b', null, ep.minRR.toFixed(1) + 'R')),
                h('span', { className: 'rr-mode' }, String(ep.targetMode).replace(/_/g, ' ')),
                liveR != null
                  ? h('span', { className: 'rr-live' }, 'live ', h('b', { className: 'num' }, liveR.toFixed(2) + 'R'))
                  : (invFlat ? h('span', { className: 'rr-live bad' }, 'below stop') : null))),

        !none && !noneReach ? h('div', { className: 'rr-prop' },
          h('div', { className: 'rr-prop-risk' }, '1R'),
          h('div', { className: 'rr-prop-rew', style: { flex: rewFlex } }, ep.blendedRR.toFixed(2) + 'R')) : null,

        // a far stop ("the entry is wrong for this level") or an ATR-mirror stop (no level on this side): said, never moved
        ep.stop && ep.stop.warning ? h('div', { className: 'rr-warn-banner' }, '⚠ ' + ep.stop.warning) : null,

        rrAnchor(ep.anchor),
        rrLadder(ep, targets, none),
        rrFoot(ep, f),
        ep.sizing ? rrSizingTable(ep.sizing, 'On a fresh buy the ladder is per-share — only size differs') : null));
  }

  // ========================== POSITION ==========================
  const P = f.position, S = f.scaleInPreview;
  const dir = P.side === 'short' ? -1 : 1;
  const rungs: any[] = P.targetsRemaining || [];
  const inv = dir === 1 ? lastPrice <= P.stop.price : lastPrice >= P.stop.price;
  const openR = P.origRisk && P.origRisk.perShare
    ? dir * (lastPrice - P.origRisk.entry) / P.origRisk.perShare
    : null;
  let up = 0;
  rungs.forEach((t: any) => {
    const beyond = dir === 1 ? t.price > lastPrice : t.price < lastPrice;
    if (beyond) up += Math.abs(t.price - lastPrice) * t.qty;
  });
  const riskDen = dir === 1 ? (lastPrice - P.stop.price) * P.qty : (P.stop.price - lastPrice) * P.qty;
  const remRR = !inv && riskDen > 0 ? up / riskDen : null;
  const openUp = openR != null && openR >= 0;

  return h('div', { className: 'pos-card rr-card wide' + (stale ? ' rr-stale' : ''), style: cardStyle, ref: cardRef },
    head(badges),
    h('div', { className: 'pc-body rr-body' },

      // hero: open R + remaining RR
      h('div', { className: 'rr-hero pos' },
        h('div', { className: 'rr-hero-main' },
          h('span', { className: 'rr-blend num ' + (openUp ? 'up' : 'down') },
            inv ? '—' : (openR != null ? (openR >= 0 ? '+' : '') + openR.toFixed(2) : '—')),
          h('span', { className: 'rr-blend-u' }, 'R'),
          h('span', { className: 'rr-open-lab' }, 'open'),
          h('span', { className: 'rr-rem' }, 'remaining ', h('b', { className: 'num' }, remRR != null ? remRR.toFixed(2) + 'R' : '—'))),
        h('div', { className: 'rr-hero-sub' },
          h('span', null, String(P.side || 'long').toUpperCase(), ' ', h('b', null, fmtQty(P.qty) + ' sh'), ' @ ', h('b', null, '$' + fmtPx4(P.entry))),
          h('span', { className: 'rr-fresh' }, 'fresh buy ', h('b', { className: 'num' }, ep.blendedRR != null ? ep.blendedRR.toFixed(2) + 'R' : '—')))),

      // position facts
      h('div', { className: 'rr-posfacts' },
        rrFact('Entry (VWAP)', '$' + fmtPx4(P.entry)),
        rrFact('Stop', '$' + fmtPx4(P.stop.price), RR_STOP_LABEL[P.stop.source] || P.stop.source),
        P.origRisk
          ? rrFact('Orig risk', '$' + P.origRisk.perShare + '/sh', 'from $' + fmtPx4(P.origRisk.entry) + ' / $' + fmtPx4(P.origRisk.stop))
          : rrFact('Orig risk', '—', 'not captured'),
        rrFact('Size', fmtQty(P.qty) + ' / ' + fmtQty(P.askedQty), 'held / asked')),

      // resting rungs
      rungs.length ? h('div', { className: 'rr-rungs' },
        h('div', { className: 'rr-sec-h' }, 'Resting targets'),
        rungs.map((t: any) => {
          const rAt = P.origRisk && P.origRisk.perShare ? dir * (t.price - lastPrice) / P.origRisk.perShare : null;
          const hit = dir === 1 ? t.price <= lastPrice : t.price >= lastPrice;
          return h('div', { className: 'rr-rung' + (hit ? ' hit' : ''), key: t.i },
            h('span', { className: 'rrg-i' }, 'T' + t.i),
            h('span', { className: 'rrg-px num' }, '$' + fmtPx4(t.price)),
            h('span', { className: 'rrg-qty mono' }, fmtQty(t.qty) + ' sh'),
            mv5Chip(t.mv5),
            h('span', { className: 'rrg-r num' + (hit ? ' hit' : '') },
              hit ? 'in reach' : (rAt != null ? (rAt >= 0 ? '+' : '') + rAt.toFixed(1) + 'R' : '—')));
        })) : null,

      // scale-in preview table
      S ? rrScaleIn(S, P) : null,
      rrFoot(ep, f)));
}

/* Memo gate — the perf fix. Terminal re-renders at up to ~60Hz on rAF-coalesced
   price ticks PLUS uncoalesced crosshair mousemoves (setHover per pointer
   event); each one used to rebuild this card's ~200-element tree even when
   nothing it reads had changed. The comparator re-renders the card ONLY when
   symbol / lastPrice / pos actually change — hover-driven parent renders are
   skipped entirely. Function props are deliberately excluded: both mounts pass
   stable setters or trivial fresh closures over stable setters
   (Terminal:1297, ChartWindow:280), so identity churn there is noise. Internal
   state (rr frames, the 30s stale tick) bypasses memo by design. */
function rrPropsEqual(prev: any, next: any): boolean {
  return prev.symbol === next.symbol
    && prev.lastPrice === next.lastPrice
    && prev.pos === next.pos
    && prev.frameOverride === next.frameOverride;
}
export const RrCard = React.memo(RrCardInner, rrPropsEqual);
(RrCard as any).displayName = 'RrCard';

/* ---------- shared bits ---------- */
function rrAnchor(a: any) {
  const prec = a.price < 1 ? 4 : 2;
  const hasQuote = a.bid > 0 && a.ask > 0;
  return h('div', { className: 'rr-anchor' },
    h('div', { className: 'rra-entry' },
      h('span', { className: 'rra-k' }, 'ENTRY'),
      h('span', { className: 'rra-v num' }, '$' + fmtPx4(a.price)),
      h('span', { className: 'rra-src' }, a.source)),
    hasQuote ? h('div', { className: 'rra-quote mono' },
      a.bid.toFixed(prec), h('span', { className: 'rra-x' }, '×'), a.ask.toFixed(prec),
      h('span', { className: 'rra-spr' }, 'est. fill ' + a.ask.toFixed(prec) + (a.spreadBp != null ? ' · ' + a.spreadBp + 'bp' : ''))) : null);
}
/** MV-5's read from the worker's RR preview: P(hold) of the target's wall / the stop's level at the picker's timeframe,
 *  P(reach) by its horizon (reader families only). Null when the preview carries none (no fresh MV-5 frame). */
function mv5Chip(m: any) {
  if (!m || typeof m.hold !== 'number') return null;
  const pct = (x: number) => Math.round(x * 100) + '%';
  return h('span', { className: 'rn-mv5 mono', title: 'MV-5 · P(hold) ' + pct(m.hold) + (typeof m.reach === 'number' ? ' · P(reach) ' + pct(m.reach) : '') },
    'MV-5 h' + pct(m.hold) + (typeof m.reach === 'number' ? ' r' + pct(m.reach) : ''));
}
function rrLadder(ep: any, targets: any[], none: boolean) {
  const node = (kind: string, lab: string, price: number, meta: any) =>
    h('div', { className: 'rr-node ' + kind, key: kind + lab },
      h('span', { className: 'rn-dot' }),
      h('span', { className: 'rn-lab' }, lab),
      h('span', { className: 'rn-px num' }, '$' + fmtPx4(price)),
      h('span', { className: 'rn-meta' }, meta));
  return h('div', { className: 'rr-ladder' + (none ? ' none' : '') },
    targets.slice().reverse().map((t: any) => node('t', 'T' + t.i, t.price,
      h(F, null,
        h('span', { className: 'rn-move mono' }, '+' + t.movePct + '%'),
        h('span', { className: 'rn-r num' }, t.r.toFixed(2) + 'R'),
        h('span', { className: 'rn-alloc' }, h('i', { style: { width: t.allocPct + '%' } }), h('em', null, t.allocPct + '%')),
        mv5Chip(t.mv5)))),
    node('entry', 'ENTRY', ep.anchor.price, h('span', { className: 'rn-flat mono' }, 'anchor · ' + ep.anchor.source)),
    node('stop', 'STOP', ep.stop.price,
      h(F, null,
        h('span', { className: 'rn-move mono' }, ep.stop.wasWidened ? 'widened' : '−$' + ep.stop.riskPerShare),
        h('span', { className: 'rn-r down num' }, '−' + ep.stop.riskPct.toFixed(2) + '%'),
        mv5Chip(ep.stop.mv5))));
}
function rrFoot(ep: any, f: any) {
  return h('div', { className: 'rr-foot' },
    ep.refPreset ? h('span', { className: 'rr-refnote' },
      ep.preset + ' preset active — ladder is an R/R reference (' + ep.refPreset + ' splits); a buy now places NO target orders.') : null,
    h('span', null, 'Stop ', h('b', null, RR_STOP_LABEL[ep.stop.type] || String(ep.stop.type || '').replace(/_/g, ' '))),
    ep.stop.supportType ? h('span', null, 'under ', h('b', null, String(ep.stop.supportType).replace(/_/g, ' ') + ' $' + fmtPx4(ep.stop.supportLevel))) : null,
    ep.sessionAtr != null ? h('span', null, 'ATR ', h('b', null, '$' + ep.sessionAtr)) : null,
    h('span', { className: 'rr-seq' }, h('i', { className: 'rr-live-dot' }), 'live · #' + f.seq + ' · ' + f.trigger + ' · ' + etClock(f.ts)));
}
function rrFact(k: string, v: any, sub?: any) {
  return h('div', { className: 'rr-fact', key: k },
    h('span', { className: 'rrf-k' }, k),
    h('span', { className: 'rrf-v num' }, v),
    sub ? h('span', { className: 'rrf-s' }, sub) : null);
}
function rrSizingTable(sizing: any, caption: string) {
  const rows = SIZE_ORDER.filter((k) => sizing.byPreset && sizing.byPreset[k]);
  return h('div', { className: 'rr-sizing' },
    h('div', { className: 'rr-sec-h' }, 'Size buttons',
      h('span', { className: 'rr-basis' }, String(sizing.basis || '').replace('_', '-'), ' · ', usd0(sizing.accountSize), ' acct')),
    h('div', { className: 'rr-sz-row rr-sz-head' },
      h('span', null, ''), h('span', null, 'QTY'), h('span', null, 'VALUE'), h('span', null, 'RISK $')),
    rows.map((k, idx) => {
      const s = sizing.byPreset[k];
      return h('div', { className: 'rr-sz-row' + (idx % 2 === 0 ? ' odd' : ''), key: k },
        h('span', { className: 'rr-sz-lab' }, k),
        h('span', { className: 'num' }, fmtQty(s.qty)),
        h('span', { className: 'num dim' }, usd0(s.value)),
        h('span', { className: 'num' + (s.riskPctOfAccount >= 0.3 ? ' warn' : '') },
          usd0(s.riskUsd), h('em', null, s.riskPctOfAccount.toFixed(2) + '%')));
    }),
    caption ? h('div', { className: 'rr-cap' }, caption) : null);
}
function rrScaleIn(S: any, P: any) {
  const prec = S.addPrice < 1 ? 4 : 2;
  const rows = SIZE_ORDER.filter((k) => S.byPreset && S.byPreset[k]);
  return h('div', { className: 'rr-scalein' },
    h('div', { className: 'rr-sec-h' }, 'Scale-in preview',
      h('span', { className: 'rr-basis' }, 'add @ $' + fmtPx4(S.addPrice))),
    S.wouldOverwriteManualStop ? h('div', { className: 'rr-warn-banner' },
      h('svg', { viewBox: '0 0 24 24', width: 13, height: 13, fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
        h('path', { d: 'M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z' })),
      'Every add replaces your manual $', fmtPx4(P.stop.price), ' stop with a recalculated one.') : null,
    h('div', { className: 'rr-si-row rr-si-head' },
      h('span', null, ''), h('span', null, '+QTY'), h('span', null, 'NEW AVG'), h('span', null, 'STOP'), h('span', null, 'RR'), h('span', null, 'RISK $')),
    rows.map((k, idx) => {
      const r = S.byPreset[k];
      const kept = r.stopSource === 'kept_existing';
      const lower = (r.stopDelta || 0) < 0;
      return h('div', { className: 'rr-si-row' + (idx % 2 === 0 ? ' odd' : ''), key: k },
        h('span', { className: 'rr-sz-lab' }, k),
        h('span', { className: 'num' }, '+' + fmtQty(r.addQty), h('em', null, fmtQty(r.newQty))),
        h('span', { className: 'num' }, '$' + fmtPx4(r.newEntry)),
        h('span', { className: 'rr-si-stop num' },
          '$' + fmtPx4(r.stop),
          kept
            ? h('em', null, 'kept')
            : h('em', { className: lower ? 'down' : 'up' }, (r.stopDelta >= 0 ? '+' : '') + r.stopDelta.toFixed(prec))),
        h('span', { className: 'num rr-si-rr' + (r.blendedRR != null && r.blendedRR >= 1.5 ? ' up' : ' down') },
          r.blendedRR != null ? r.blendedRR.toFixed(2) : '—'),
        h('span', { className: 'num' + (r.riskPct >= 4 ? ' warn' : '') },
          usd0(r.riskUsd), h('em', null, r.riskPct != null ? r.riskPct.toFixed(2) + '%' : '')));
    }),
    h('div', { className: 'rr-cap' }, 'Stop re-picks off the blended entry — risk is non-monotonic by size. Preview at mid; real recalc runs at the fill.'));
}
